import * as React from 'react';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Divider,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  MenuBook as BookIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  Description as OutlineIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { NovelWritingService } from '../services/novelWritingService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { AssetRetrievalService } from '../services/assetRetrievalService';
import {
  NovelSession,
  NovelSessionStorage,
  StartNovelSessionRequest,
  NovelCharacter,
  NovelLocation,
  NovelChapter,
  CreateCharacterProfileRequest,
  CreateLocationRequest,
  GenerateNovelChapterRequest,
  NOVEL_GENRES,
  WRITING_STYLES,
  POV_OPTIONS,
  TENSE_OPTIONS,
  CHARACTER_ROLES,
  LOCATION_TYPES,
  NovelOutline,
  ChapterOutline,
} from '../models/novelWriting';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`novel-tabpanel-${index}`}
      aria-labelledby={`novel-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const parseChaptersFromOverview = (overview: string): ChapterOutline[] => {
  const chapters: ChapterOutline[] = [];

  console.log('Parsing chapters from overview, length:', overview.length);
  console.log('First 500 chars of overview:', overview.substring(0, 500));

  // Helper function to clean up chapter titles
  const cleanTitle = (title: string): string => {
    return title
      .trim()
      // Remove markdown bold/italic markers
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      // Remove trailing "Summary" text
      .replace(/\s*Summary\s*$/i, '')
      // Remove any remaining special characters at the end
      .replace(/[\s_-]+$/, '')
      .trim();
  };

  // Multiple patterns to try - the API might use different formats
  const patterns = [
    // Pattern 1: Chapter 1: The Title (Perspective): Description
    /^Chapter\s+(\d+):\s*([^(]+?)(?:\s*\([^)]+\))?\s*:\s*(.+)$/gim,
    // Pattern 2: *   **Chapter 1: Title:** Description
    /\*\s+\*\*Chapter\s+(\d+):\s*([^*:]+)\*\*:\s*([^\n]+)/gi,
    // Pattern 3: * **Chapter 1: Title:** Description
    /\*\s*\*\*Chapter\s+(\d+):\s*([^*:]+)\*\*:\s*([^\n]+)/gi,
    // Pattern 4: **Chapter 1: Title:** Description (no bullet)
    /\*\*Chapter\s+(\d+):\s*([^*:]+)\*\*:\s*([^\n]+)/gi,
    // Pattern 5: Chapter 1: Title: Description (simple colon format)
    /Chapter\s+(\d+):\s*([^:]+?):\s*(.+)/gi
  ];

  let matchCount = 0;

  for (const pattern of patterns) {
    console.log('Trying pattern:', pattern.source);
    pattern.lastIndex = 0; // Reset regex state
    let match;

    while ((match = pattern.exec(overview)) !== null) {
      matchCount++;
      const chapterNumber = parseInt(match[1]);
      const title = cleanTitle(match[2]);
      const summary = match[3].trim();
      
      console.log(`Found chapter ${chapterNumber}: "${title}" - "${summary.substring(0, 50)}..."`);
      
      // Check if we already have this chapter (avoid duplicates)
      if (!chapters.some(ch => ch.chapterNumber === chapterNumber)) {
        chapters.push({
          chapterNumber,
          title,
          summary,
          keyEvents: [],
          charactersPresent: [],
          locationsPresent: [],
          plotProgression: ''
        });
      }
    }
    
    // If we found chapters with this pattern, stop trying other patterns
    if (chapters.length > 0) {
      console.log(`Successfully matched with pattern: ${pattern.source}`);
      break;
    }
  }
  
  console.log(`Total chapters parsed: ${chapters.length} from ${matchCount} matches`);
  
  // Sort by chapter number
  chapters.sort((a, b) => a.chapterNumber - b.chapterNumber);
  
  return chapters;
};

interface ParsedCharacter {
  name: string;
  description: string;
  role?: string;
}

const parseCharactersFromOverview = (overview: string): ParsedCharacter[] => {
  const characters: ParsedCharacter[] = [];
  const addedNames = new Set<string>();
  
  // Look for Main Character Arcs section
  const characterArcsMatch = overview.match(/\*\*Main Character Arcs:\*\*([\s\S]*?)(?=\*\*[A-Z]|\n\n\*\*|$)/);
  
  if (characterArcsMatch) {
    const arcsSection = characterArcsMatch[1];
    
    // Pattern: *   **CharacterName:** Description of their arc...
    const characterPattern = /\*\s*\*\*([^:*]+)\*\*:\s*([^\n]+(?:\n(?!\s*\*).*)*)/gi;
    let match;
    
    while ((match = characterPattern.exec(arcsSection)) !== null) {
      const name = match[1].trim();
      const description = match[2].trim().replace(/\n/g, ' ');
      
      if (!addedNames.has(name)) {
        addedNames.add(name);
        characters.push({
          name,
          description,
          role: 'Main Character'
        });
      }
    }
  }
  
  // Also look for characters mentioned in Setting or other sections
  // Pattern: **CharacterName (Role):** Description
  const additionalCharacterPattern = /\*\*([^(*]+)(?:\s*\(([^)]+)\))?\*\*:\s*([^\n]+)/gi;
  let match2;
  
  while ((match2 = additionalCharacterPattern.exec(overview)) !== null) {
    const name = match2[1].trim();
    const role = match2[2] ? match2[2].trim() : undefined;
    const description = match2[3].trim();
    
    // Filter out non-character entries (like "Synopsis", "Part 1", etc.)
    if (!name.includes('Chapter') && !name.includes('Part') && 
        !name.includes('Synopsis') && !name.includes('Theme') && 
        !name.includes('Setting') && !name.includes('Outline') &&
        !addedNames.has(name) && name.length < 30) {
      addedNames.add(name);
      characters.push({
        name,
        description,
        role
      });
    }
  }
  
  return characters;
};

const NovelWritingAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch persisted sessions and assets from backend
  const {
    sessions: backendSessions,
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'NovelSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true,
  });

  const [tabValue, setTabValue] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [title, setTitle] = useState('');
  const [concept, setConcept] = useState('');
  const [genre, setGenre] = useState('');
  const [style, setStyle] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(80000);
  const [chapterCount, setChapterCount] = useState(20);
  const [pov, setPov] = useState('Third Person Limited');
  const [tense, setTense] = useState('Past Tense');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [maxTokensPerTurn, setMaxTokensPerTurn] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<NovelSession | null>(null);
  const [sessions, setSessions] = useState<NovelSession[]>([]);
  const [characterDialog, setCharacterDialog] = useState(false);
  const [locationDialog, setLocationDialog] = useState(false);
  const [chapterDialog, setChapterDialog] = useState(false);
  const [viewChapterDialog, setViewChapterDialog] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [viewingChapter, setViewingChapter] = useState<NovelChapter | null>(null);

  // Character dialog state
  const [characterName, setCharacterName] = useState('');
  const [characterRole, setCharacterRole] = useState('');
  const [characterDescription, setCharacterDescription] = useState('');
  const [characterBackstory, setCharacterBackstory] = useState('');
  const [characterPersonality, setCharacterPersonality] = useState('');
  const [characterRelationships, setCharacterRelationships] = useState('');
  const [characterArc, setCharacterArc] = useState('');

  // Location dialog state
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [locationSignificance, setLocationSignificance] = useState('');
  const [locationAtmosphere, setLocationAtmosphere] = useState('');

  // Chapter dialog state
  const [chapterTitle, setChapterTitle] = useState('');
  const [chapterSummary, setChapterSummary] = useState('');
  const [chapterCharacters, setChapterCharacters] = useState<string[]>([]);
  const [chapterLocations, setChapterLocations] = useState<string[]>([]);
  const [chapterInstructions, setChapterInstructions] = useState('');
  const [chapterWordCount, setChapterWordCount] = useState(4000);

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    const loadSessions = () => {
      const localSessions: NovelSession[] = [];

      // Load from localStorage
      const stored = localStorage.getItem('novelWritingSessions');
      if (stored) {
        const data: NovelSessionStorage = JSON.parse(stored);
        localSessions.push(...data.sessions.map(s => ({
          ...s,
          createdAt: new Date(s.createdAt)
        })));
      }

      // Merge with backend sessions
      if (backendSessions && backendSessions.length > 0) {
        // Convert backend sessions to NovelSession format
        const convertedBackendSessions: NovelSession[] = backendSessions.map(bs => {
          // Parse sessionData JSON string to extract novel data
          let sessionData: any = {};
          try {
            if (bs.session.sessionData) {
              sessionData = JSON.parse(bs.session.sessionData);
            }
          } catch (e) {
            console.error('[NovelWritingAgent] Failed to parse sessionData:', e);
          }

          // Parse outline if it's a string
          let outline = sessionData.Outline || bs.session.metadata?.outline || { overview: '', chapters: [] };
          if (typeof outline === 'string' && outline) {
            try {
              outline = JSON.parse(outline);
            } catch (e) {
              outline = { overview: outline, chapters: [] };
            }
          }

          return {
            id: bs.session.id,
            sessionId: bs.session.id,
            title: sessionData.Title || bs.session.title || bs.session.metadata?.title || 'Untitled Novel',
            genre: sessionData.Genre || bs.session.genre || bs.session.metadata?.genre || '',
            concept: sessionData.Concept || bs.session.concept || bs.session.metadata?.concept || '',
            style: sessionData.Style || bs.session.style || bs.session.metadata?.style,
            theme: sessionData.Theme || bs.session.metadata?.theme,
            targetAudience: sessionData.TargetAudience || bs.session.metadata?.targetAudience,
            synopsis: sessionData.Synopsis || bs.session.metadata?.synopsis,
            outline: outline,
            characters: sessionData.Characters || bs.assets
              .filter(a => a.subType === 'Character')
              .map(a => JSON.parse(a.textContent || '{}')) || [],
            locations: sessionData.Locations || bs.assets
              .filter(a => a.subType === 'Location')
              .map(a => JSON.parse(a.textContent || '{}')) || [],
            chapters: sessionData.Chapters || bs.assets
              .filter(a => a.subType === 'Chapter')
              .map(a => JSON.parse(a.textContent || '{}')) || [],
            metadata: bs.session.metadata || {},
            createdAt: new Date(bs.session.createdAt),
            updatedAt: bs.session.updatedAt ? new Date(bs.session.updatedAt) : undefined,
          };
        });

        // Merge: prefer backend sessions (they're authoritative)
        const sessionMap = new Map<string, NovelSession>();

        // Add local sessions first
        localSessions.forEach(s => sessionMap.set(s.id || s.sessionId || '', s));

        // Override with backend sessions
        convertedBackendSessions.forEach(s => sessionMap.set(s.id, s));

        setSessions(Array.from(sessionMap.values()));
      } else {
        setSessions(localSessions);
      }
    };

    loadSessions();
  }, [backendSessions]);

  // Save sessions to localStorage
  const saveSessions = (newSessions: NovelSession[]) => {
    const storage: NovelSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('novelWritingSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleStartSession = async () => {
    if (!title.trim() || !concept.trim()) {
      setError('Please provide a title and concept');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: StartNovelSessionRequest = {
        title: title.trim(),
        concept: concept.trim(),
        genre: genre || undefined,
        style: style || undefined,
        targetAudience: targetAudience.trim() || undefined,
        targetWordCount,
        chapterCount,
        pov,
        tense,
        additionalInstructions: additionalInstructions.trim() || undefined,
        maxTokensPerTurn
      };

      const response = await NovelWritingService.startSession(request);
      
      if (response.success && response.sessionId) {
        setSessionId(response.sessionId);
        
        // Store the raw overview without parsing
        let parsedOutline: NovelOutline | undefined;
        if (response.overview) {
          parsedOutline = {
            title: response.title || title.trim(),
            premise: response.overview, // Store the full overview as premise for now
            themes: [],
            chapters: []
          };
        }
        
        const newSession: NovelSession = {
          id: Date.now().toString(),
          sessionId: response.sessionId,
          title: response.title || title.trim(),
          concept: concept.trim(),
          genre: genre || undefined,
          style: style || undefined,
          targetWordCount,
          currentWordCount: 0,
          outline: parsedOutline || response.initialOutline,
          characters: [],
          locations: [],
          chapters: [],
          createdAt: new Date()
        };
        
        setCurrentSession(newSession);
        saveSessions([newSession, ...sessions]);
        setActiveStep(1);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start novel writing session');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!sessionId || !currentSession) return;

    setLoading(true);
    setError(null);

    try {
      const response = await NovelWritingService.generateOutline(sessionId, {
        includeCharacterDescriptions: true,
        includeLocationDescriptions: true,
        includeThemes: true,
        includeSubplots: true,
        detailLevel: 'detailed',
        maxTokens: maxTokensPerTurn
      });
      console.log('Outline generation response:', response);
      if (response.success) {
        // Parse chapters from overview for the Write Chapters tab
        const parsedChapters = parseChaptersFromOverview(response.overview || '');
        
        // Parse characters from overview
        const parsedCharacters = parseCharactersFromOverview(response.overview || '');
        
        const updatedSession = {
          ...currentSession,
          outline: {
            title: response.title || currentSession.title,
            premise: response.overview, // Use the overview field which contains the full markdown
            themes: [],
            chapters: parsedChapters
          },
          // Store parsed characters with basic info (they can be expanded later)
          characters: parsedCharacters.map(char => ({
            name: char.name,
            description: char.description,
            role: char.role
          }))
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        setActiveStep(2);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate outline');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCharacter = async () => {
    if (!sessionId || !currentSession || !characterName.trim()) {
      setError('Please provide a character name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: CreateCharacterProfileRequest = {
        name: characterName.trim(),
        role: characterRole || undefined,
        description: characterDescription.trim() || undefined,
        backstory: characterBackstory.trim() || undefined,
        personality: characterPersonality.trim() || undefined,
        relationships: characterRelationships.trim() || undefined,
        arc: characterArc.trim() || undefined,
        maxTokens: maxTokensPerTurn
      };

      const response = await NovelWritingService.createCharacterProfile(sessionId, request);
      
      if (response.success && response.character) {
        const updatedSession = {
          ...currentSession,
          characters: [...currentSession.characters, response.character]
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        // Reset character dialog
        setCharacterDialog(false);
        setCharacterName('');
        setCharacterRole('');
        setCharacterDescription('');
        setCharacterBackstory('');
        setCharacterPersonality('');
        setCharacterRelationships('');
        setCharacterArc('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create character');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!sessionId || !currentSession || !locationName.trim()) {
      setError('Please provide a location name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: CreateLocationRequest = {
        name: locationName.trim(),
        type: locationType || undefined,
        description: locationDescription.trim() || undefined,
        significance: locationSignificance.trim() || undefined,
        atmosphere: locationAtmosphere.trim() || undefined,
        maxTokens: maxTokensPerTurn
      };

      const response = await NovelWritingService.createLocation(sessionId, request);
      
      if (response.success && response.location) {
        const updatedSession = {
          ...currentSession,
          locations: [...currentSession.locations, response.location]
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        // Reset location dialog
        setLocationDialog(false);
        setLocationName('');
        setLocationType('');
        setLocationDescription('');
        setLocationSignificance('');
        setLocationAtmosphere('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateChapter = async () => {
    if (!sessionId || !currentSession || selectedChapter === null) return;

    setLoading(true);
    setError(null);

    try {
      const request: GenerateNovelChapterRequest = {
        chapterNumber: selectedChapter,
        chapterTitle: chapterTitle.trim() || undefined,
        chapterSummary: chapterSummary.trim() || undefined,
        charactersPresent: chapterCharacters.length > 0 ? chapterCharacters : undefined,
        locationsPresent: chapterLocations.length > 0 ? chapterLocations : undefined,
        specificInstructions: chapterInstructions.trim() || undefined,
        targetWordCount: chapterWordCount,
        maxTokens: maxTokensPerTurn
      };

      const response = await NovelWritingService.generateChapter(sessionId, request);
      
      if (response.success && response.chapter) {
        const updatedChapters = [...currentSession.chapters];
        const existingIndex = updatedChapters.findIndex(ch => ch.chapterNumber === selectedChapter);
        
        if (existingIndex >= 0) {
          updatedChapters[existingIndex] = response.chapter;
        } else {
          updatedChapters.push(response.chapter);
          updatedChapters.sort((a, b) => a.chapterNumber - b.chapterNumber);
        }
        
        const newWordCount = updatedChapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0);
        
        const updatedSession = {
          ...currentSession,
          chapters: updatedChapters,
          currentWordCount: newWordCount
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        // Reset chapter dialog
        setChapterDialog(false);
        setChapterTitle('');
        setChapterSummary('');
        setChapterCharacters([]);
        setChapterLocations([]);
        setChapterInstructions('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleExportNovel = () => {
    if (!currentSession) return;

    let content = `# ${currentSession.title}\n\n`;
    
    if (currentSession.genre || currentSession.style) {
      content += `**Genre:** ${currentSession.genre || 'Not specified'}\n`;
      content += `**Style:** ${currentSession.style || 'Not specified'}\n\n`;
    }
    
    content += `## Concept\n${currentSession.concept}\n\n`;
    
    if (currentSession.outline) {
      content += `## Outline\n\n`;
      content += `### Premise\n${currentSession.outline.premise || 'Not generated'}\n\n`;
      
      if (currentSession.outline.themes && currentSession.outline.themes.length > 0) {
        content += `### Themes\n`;
        currentSession.outline.themes.forEach(theme => {
          content += `- ${theme}\n`;
        });
        content += '\n';
      }
    }
    
    if (currentSession.characters.length > 0) {
      content += `## Characters\n\n`;
      currentSession.characters.forEach(char => {
        content += `### ${char.name}\n`;
        if (char.role) content += `**Role:** ${char.role}\n`;
        if (char.description) content += `${char.description}\n`;
        if (char.personality) content += `\n**Personality:** ${char.personality}\n`;
        if (char.backstory) content += `\n**Backstory:** ${char.backstory}\n`;
        content += '\n---\n\n';
      });
    }
    
    if (currentSession.locations.length > 0) {
      content += `## Locations\n\n`;
      currentSession.locations.forEach(loc => {
        content += `### ${loc.name}\n`;
        if (loc.type) content += `**Type:** ${loc.type}\n`;
        if (loc.description) content += `${loc.description}\n`;
        if (loc.atmosphere) content += `\n**Atmosphere:** ${loc.atmosphere}\n`;
        content += '\n---\n\n';
      });
    }
    
    if (currentSession.chapters.length > 0) {
      content += `## Chapters\n\n`;
      currentSession.chapters.forEach(chapter => {
        content += `### Chapter ${chapter.chapterNumber}: ${chapter.title || 'Untitled'}\n\n`;
        if (chapter.summary) {
          content += `**Summary:** ${chapter.summary}\n\n`;
        }
        content += chapter.content + '\n\n';
        content += `---\n\n`;
      });
    }
    
    content += `\n**Word Count:** ${currentSession.currentWordCount || 0} / ${currentSession.targetWordCount || 80000}\n`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSession.title.replace(/\s+/g, '-').toLowerCase()}-novel.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyChapter = (chapter: NovelChapter) => {
    const text = `Chapter ${chapter.chapterNumber}: ${chapter.title || 'Untitled'}\n\n${chapter.content}`;
    navigator.clipboard.writeText(text);
  };

  const handleLoadSession = (session: NovelSession) => {
    setCurrentSession(session);
    setSessionId(session.sessionId || null);
    setTitle(session.title);
    setConcept(session.concept || '');
    setGenre(session.genre || '');
    setStyle(session.style || '');
    setTargetWordCount(session.targetWordCount || 80000);
    setActiveStep(session.chapters.length > 0 ? 3 : session.outline ? 2 : 1);
    setTabValue(1); // Switch to workspace tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  const handleReset = () => {
    setTitle('');
    setConcept('');
    setGenre('');
    setStyle('');
    setTargetAudience('');
    setTargetWordCount(80000);
    setChapterCount(20);
    setPov('Third Person Limited');
    setTense('Past Tense');
    setAdditionalInstructions('');
    setMaxTokensPerTurn(2048);
    setSessionId(null);
    setCurrentSession(null);
    setActiveStep(0);
    setTabValue(0);
  };

  const getWordCountProgress = () => {
    if (!currentSession) return 0;
    const current = currentSession.currentWordCount || 0;
    const target = currentSession.targetWordCount || 80000;
    return (current / target) * 100;
  };

  const getChapterProgress = () => {
    if (!currentSession || !currentSession.outline?.chapters) return 0;
    const totalChapters = currentSession.outline.chapters.length;
    const writtenChapters = currentSession.chapters.length;
    return (writtenChapters / totalChapters) * 100;
  };

  const handleContinueToWriting = () => {
    if (!currentSession) return;
    
    // If we have an outline but no chapters parsed, try to parse them
    if (currentSession.outline?.premise && (!currentSession.outline.chapters || currentSession.outline.chapters.length === 0)) {
      console.log('No chapters found, attempting to parse from outline...');
      const parsedChapters = parseChaptersFromOverview(currentSession.outline.premise);
      
      if (parsedChapters.length > 0) {
        console.log(`Parsed ${parsedChapters.length} chapters, updating session`);
        const updatedSession = {
          ...currentSession,
          outline: {
            ...currentSession.outline,
            chapters: parsedChapters
          }
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
      } else {
        console.warn('Failed to parse chapters from outline');
      }
    }
    
    // Proceed to Write Chapters step
    setActiveStep(3);
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexDirection: { xs: 'row', sm: 'row' },
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <BookIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Novel Writing Assistant
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Create novels with AI assistance' : 'Create full-length novels with AI-powered chapter generation and character development'}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          {...(isMobile && { scrollButtons: 'auto' })}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: { xs: 44, sm: 48 }
            }
          }}
        >
          <Tab label={isMobile ? "New" : "Start New Novel"} />
          <Tab label={isMobile ? "Workspace" : "Novel Workspace"} disabled={!currentSession} />
          <Tab label="History" icon={<HistoryIcon fontSize={isMobile ? 'small' : 'medium'} />} iconPosition="start" />
        </Tabs>
      </Paper>

        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              label="Novel Title"
              placeholder={isMobile ? "e.g., The Last Kingdom" : "e.g., The Last Kingdom"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label="Novel Concept"
              placeholder={isMobile ? "Describe your novel's concept..." : "Describe your novel's concept, main plot, and central conflict..."}
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Genre</InputLabel>
                  <Select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    label="Genre"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                    {NOVEL_GENRES.map((g) => (
                      <MenuItem key={g} value={g} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Writing Style</InputLabel>
                  <Select
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                    label="Writing Style"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                    {WRITING_STYLES.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Point of View</InputLabel>
                  <Select
                    value={pov}
                    onChange={(e) => setPov(e.target.value)}
                    label="Point of View"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {POV_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Tense</InputLabel>
                  <Select
                    value={tense}
                    onChange={(e) => setTense(e.target.value)}
                    label="Tense"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {TENSE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Target Audience"
              placeholder="e.g., Young Adults, Fantasy Readers"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={isMobile ? "Word Count" : "Target Word Count"}
                  value={targetWordCount}
                  onChange={(e) => setTargetWordCount(parseInt(e.target.value) || 80000)}
                  inputProps={{ min: 20000, max: 200000, step: 5000 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={isMobile ? "Chapters" : "Chapter Count"}
                  value={chapterCount}
                  onChange={(e) => setChapterCount(parseInt(e.target.value) || 20)}
                  inputProps={{ min: 5, max: 50 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={isMobile ? "Max Tokens" : "Max Tokens per Turn"}
                  value={maxTokensPerTurn}
                  onChange={(e) => setMaxTokensPerTurn(parseInt(e.target.value) || 2048)}
                  inputProps={{ min: 1024, max: 4096 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Additional Instructions"
              placeholder={isMobile ? "Any specific requirements..." : "Any specific requirements, themes, or style preferences..."}
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
              <Button
                variant="contained"
                onClick={handleStartSession}
                disabled={loading || !title.trim() || !concept.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <StartIcon />)}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {loading ? 'Starting...' : (isMobile ? 'Start Project' : 'Start Novel Project')}
              </Button>
            </Box>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {currentSession && (
              <>
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {currentSession.title}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 2 }}>
                  <Chip 
                    label={currentSession.genre || 'No genre'} 
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                  />
                  <Chip 
                    label={currentSession.style || 'No style'} 
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                  />
                  <Chip 
                    label={`${currentSession.chapters.length} ${isMobile ? 'ch.' : 'chapters written'}`} 
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Words' : 'Word Count Progress'}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={getWordCountProgress()} 
                    sx={{ height: { xs: 8, sm: 10 }, borderRadius: 5 }}
                  />
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    {(currentSession.currentWordCount || 0).toLocaleString()} / {(currentSession.targetWordCount || 80000).toLocaleString()} words
                  </Typography>
                </Box>
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Chapters' : 'Chapter Progress'}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={getChapterProgress()} 
                    sx={{ height: { xs: 8, sm: 10 }, borderRadius: 5 }}
                  />
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    {currentSession.chapters.length} / {currentSession.outline?.chapters?.length || chapterCount} chapters
                  </Typography>
                </Box>
              </Box>

              <Stepper activeStep={activeStep} orientation="vertical">
                <Step>
                  <StepLabel>Novel Setup</StepLabel>
                  <StepContent>
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Novel project created successfully!
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button 
                        variant="contained" 
                        onClick={() => setActiveStep(1)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        {isMobile ? 'Continue' : 'Continue to Outline'}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Generate Outline</StepLabel>
                  <StepContent>
                    <Typography 
                      paragraph
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile 
                        ? 'Generate outline with chapters, characters, and locations.'
                        : 'Generate a comprehensive outline including chapter summaries, character profiles, and locations.'
                      }
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
                      <Button
                        variant="contained"
                        onClick={handleGenerateOutline}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <OutlineIcon />)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        {loading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate Outline')}
                      </Button>
                      <Button 
                        onClick={() => setActiveStep(0)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Develop World & Characters</StepLabel>
                  <StepContent>
                    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                      {currentSession.outline && (
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                              Novel Outline
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {/* Raw Overview Display */}
                            {currentSession.outline.premise && (
                              <Box sx={{ 
                                '& h1': { fontSize: { xs: '1.25rem', sm: '1.5rem' }, mt: 2, mb: 1 },
                                '& h2': { fontSize: { xs: '1.125rem', sm: '1.3rem' }, mt: 2, mb: 1 },
                                '& h3': { fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 1.5, mb: 0.5 },
                                '& h4': { fontSize: { xs: '0.9375rem', sm: '1rem' }, mt: 1, mb: 0.5 },
                                '& p': { mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } },
                                '& ul, & ol': { mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } },
                                '& li': { ml: 2 },
                                '& strong': { fontWeight: 600 },
                                '& blockquote': { 
                                  borderLeft: '3px solid',
                                  borderColor: 'primary.main',
                                  pl: 2,
                                  ml: 0,
                                  my: 1,
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }
                              }}>
                                <ReactMarkdown>{currentSession.outline.premise}</ReactMarkdown>
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
                        <Button
                          startIcon={!isMobile && <PersonIcon />}
                          onClick={() => setCharacterDialog(true)}
                          variant="outlined"
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Add Character
                        </Button>
                        <Button
                          startIcon={!isMobile && <PlaceIcon />}
                          onClick={() => setLocationDialog(true)}
                          variant="outlined"
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Add Location
                        </Button>
                      </Box>

                      {currentSession.characters.length > 0 && (
                        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                          <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                          >
                            Characters
                          </Typography>
                          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {currentSession.characters.map((char, i) => (
                              <Grid item xs={12} md={6} key={i}>
                                <Card>
                                  <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                                    <Typography 
                                      variant="h6"
                                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                    >
                                      {char.name}
                                    </Typography>
                                    {char.role && (
                                      <Typography 
                                        variant="subtitle2" 
                                        color="primary"
                                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                      >
                                        {char.role}
                                      </Typography>
                                    )}
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        mt: 1,
                                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                                      }}
                                    >
                                      {char.description}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      {currentSession.locations.length > 0 && (
                        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                          <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                          >
                            Locations
                          </Typography>
                          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {currentSession.locations.map((loc, i) => (
                              <Grid item xs={12} md={6} key={i}>
                                <Card>
                                  <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                                    <Typography 
                                      variant="h6"
                                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                    >
                                      {loc.name}
                                    </Typography>
                                    {loc.type && (
                                      <Typography 
                                        variant="subtitle2" 
                                        color="secondary"
                                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                      >
                                        {loc.type}
                                      </Typography>
                                    )}
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        mt: 1,
                                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                                      }}
                                    >
                                      {loc.description}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
                      <Button
                        variant="contained"
                        onClick={handleContinueToWriting}
                        disabled={!currentSession.outline}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        {isMobile ? 'Continue' : 'Continue to Writing'}
                      </Button>
                      <Button 
                        onClick={() => setActiveStep(1)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Write Chapters</StepLabel>
                  <StepContent>
                    <Box>
                      <Typography 
                        paragraph
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {isMobile 
                          ? 'Select a chapter to write. Write in any order.'
                          : 'Select a chapter from the outline to write. You can write chapters in any order.'
                        }
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={!isMobile && <EditIcon />}
                          onClick={() => {
                            setSelectedChapter(null);
                            setChapterTitle('');
                            setChapterSummary('');
                            setChapterCharacters([]);
                            setChapterLocations([]);
                            setChapterDialog(true);
                          }}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          {isMobile ? 'New Chapter' : 'Write New Chapter'}
                        </Button>
                      </Box>

                      {currentSession.outline?.chapters && (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {currentSession.outline.chapters.map((ch) => {
                            const writtenChapter = currentSession.chapters.find(
                              c => c.chapterNumber === ch.chapterNumber
                            );
                            const isWritten = !!writtenChapter;

                            return (
                              <Grid item xs={12} md={6} key={ch.chapterNumber}>
                                <Card 
                                  sx={{ 
                                    opacity: isWritten ? 0.8 : 1,
                                    border: isWritten ? '2px solid' : 'none',
                                    borderColor: 'success.main'
                                  }}
                                >
                                  <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography 
                                          variant="h6"
                                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                        >
                                          {isMobile ? `Ch. ${ch.chapterNumber}` : `Chapter ${ch.chapterNumber}`}
                                        </Typography>
                                        <Typography 
                                          variant="subtitle1"
                                          sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}
                                        >
                                          {ch.title}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary" 
                                          sx={{ 
                                            mt: 1,
                                            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                                          }}
                                        >
                                          {ch.summary}
                                        </Typography>
                                        {isWritten && (
                                          <Typography 
                                            variant="caption" 
                                            color="success.main"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            ✓ Written ({writtenChapter.wordCount?.toLocaleString()} words)
                                          </Typography>
                                        )}
                                      </Box>
                                      {isWritten && (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <Tooltip title="View Chapter">
                                            <IconButton
                                              onClick={() => {
                                                setViewingChapter(writtenChapter);
                                                setViewChapterDialog(true);
                                              }}
                                              size="small"
                                              sx={{ minWidth: 44, minHeight: 44 }}
                                            >
                                              <ViewIcon />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Copy Chapter">
                                            <IconButton
                                              onClick={() => handleCopyChapter(writtenChapter)}
                                              size="small"
                                              sx={{ minWidth: 44, minHeight: 44 }}
                                            >
                                              <CopyIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Box>
                                  </CardContent>
                                  <CardActions>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setSelectedChapter(ch.chapterNumber);
                                        setChapterTitle(ch.title || '');
                                        setChapterSummary(ch.summary || '');
                                        setChapterDialog(true);
                                      }}
                                      sx={{ 
                                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                        minHeight: 44
                                      }}
                                    >
                                      {isWritten ? 'Rewrite' : 'Write'} Chapter
                                    </Button>
                                  </CardActions>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}

                      <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
                        <Button
                          variant="contained"
                          onClick={handleExportNovel}
                          startIcon={!isMobile && <DownloadIcon />}
                          disabled={currentSession.chapters.length === 0}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          {isMobile ? 'Export' : 'Export Novel'}
                        </Button>
                        <Button 
                          onClick={() => setActiveStep(2)}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Back
                        </Button>
                      </Box>
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>
            </>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ cursor: 'pointer', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}
                  onClick={() => handleLoadSession(session)}
                >
                  <ListItemText
                    primary={session.title}
                    secondary={
                      <>
                        {session.genre && `${session.genre} • `}
                        {session.style && `${session.style} • `}
                        {session.chapters.length} {isMobile ? 'ch.' : 'chapters'} • 
                        {(session.currentWordCount || 0).toLocaleString()} words
                        {!isMobile && ` • Created: ${session.createdAt.toLocaleDateString()}`}
                      </>
                    }
                    primaryTypographyProps={{
                      sx: { fontSize: { xs: '0.9375rem', sm: '1rem' } }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: { xs: '0.8125rem', sm: '0.875rem' } }
                    }}
                  />
                  <ListItemSecondaryAction sx={{ position: { xs: 'static', sm: 'absolute' }, mt: { xs: 1, sm: 0 } }}>
                    <Tooltip title="Export">
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSession(session);
                          handleExportNovel();
                        }}
                        sx={{ mr: 1, minWidth: 44, minHeight: 44 }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <BookIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                {isMobile ? 'No novels yet' : 'No novels created yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}
              >
                {isMobile ? 'Start a new novel project' : 'Your novel projects will appear here'}
              </Typography>
            </Box>
            )}
          </Paper>
        </TabPanel>

      {/* Character Dialog */}
      <Dialog 
        open={characterDialog} 
        onClose={() => setCharacterDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Create Character
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <TextField
              fullWidth
              label="Character Name"
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              autoFocus
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Role</InputLabel>
              <Select
                value={characterRole}
                onChange={(e) => setCharacterRole(e.target.value)}
                label="Role"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {CHARACTER_ROLES.map((role) => (
                  <MenuItem key={role} value={role} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Description"
              placeholder={isMobile ? "Physical appearance..." : "Physical appearance and initial impression..."}
              value={characterDescription}
              onChange={(e) => setCharacterDescription(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Personality"
              placeholder={isMobile ? "Key traits..." : "Key personality traits, mannerisms..."}
              value={characterPersonality}
              onChange={(e) => setCharacterPersonality(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Backstory"
              placeholder={isMobile ? "Character history..." : "Character's history and background..."}
              value={characterBackstory}
              onChange={(e) => setCharacterBackstory(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Relationships"
              placeholder={isMobile ? "Character connections..." : "Connections to other characters..."}
              value={characterRelationships}
              onChange={(e) => setCharacterRelationships(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Character Arc"
              placeholder={isMobile ? "Character development..." : "How the character changes throughout the story..."}
              value={characterArc}
              onChange={(e) => setCharacterArc(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: { xs: 2, sm: 1 } }}>
          <Button 
            onClick={() => setCharacterDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCharacter} 
            variant="contained" 
            disabled={!characterName.trim() || loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {isMobile ? 'Create' : 'Create Character'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Dialog */}
      <Dialog 
        open={locationDialog} 
        onClose={() => setLocationDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Create Location
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <TextField
              fullWidth
              label="Location Name"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              autoFocus
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Type</InputLabel>
              <Select
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                label="Type"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {LOCATION_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Description"
              placeholder={isMobile ? "Visual details..." : "Visual details, layout, notable features..."}
              value={locationDescription}
              onChange={(e) => setLocationDescription(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Atmosphere"
              placeholder={isMobile ? "Mood and feeling..." : "The mood and feeling of this place..."}
              value={locationAtmosphere}
              onChange={(e) => setLocationAtmosphere(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Significance"
              placeholder={isMobile ? "Story importance..." : "Why this location matters to the story..."}
              value={locationSignificance}
              onChange={(e) => setLocationSignificance(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: { xs: 2, sm: 1 } }}>
          <Button 
            onClick={() => setLocationDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateLocation} 
            variant="contained" 
            disabled={!locationName.trim() || loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {isMobile ? 'Create' : 'Create Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chapter Writing Dialog */}
      <Dialog 
        open={chapterDialog} 
        onClose={() => setChapterDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Write Chapter' : 'Write Chapter'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            {/* Debug info */}
            <Alert 
              severity="info"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
            >
              Debug: Session exists: {currentSession ? 'Yes' : 'No'} | 
              Outline exists: {currentSession?.outline ? 'Yes' : 'No'} | 
              Chapters: {currentSession?.outline?.chapters?.length || 0}
            </Alert>
            
            {/* Chapter Selection Dropdown */}
            {currentSession?.outline?.chapters && currentSession.outline.chapters.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Chapter' : 'Select Chapter'}
                </InputLabel>
                <Select
                  value={selectedChapter || ''}
                  onChange={(e) => {
                    const chapterNum = Number(e.target.value);
                    setSelectedChapter(chapterNum);
                    // Pre-populate chapter info when selected
                    const chapter = currentSession.outline?.chapters?.find(ch => ch.chapterNumber === chapterNum);
                    if (chapter) {
                      setChapterTitle(chapter.title || '');
                      setChapterSummary(chapter.summary || '');
                    }
                  }}
                  label={isMobile ? 'Chapter' : 'Select Chapter'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {currentSession.outline.chapters.map((ch) => {
                    const isWritten = currentSession.chapters.some(c => c.chapterNumber === ch.chapterNumber);
                    return (
                      <MenuItem key={ch.chapterNumber} value={ch.chapterNumber} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography sx={{ flexGrow: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {isMobile ? `Ch. ${ch.chapterNumber}: ${ch.title}` : `Chapter ${ch.chapterNumber}: ${ch.title}`}
                          </Typography>
                          {isWritten && (
                            <Chip 
                              label="Written" 
                              size="small" 
                              color="success" 
                              sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} 
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                type="number"
                label="Chapter Number"
                placeholder={isMobile ? "e.g., 1, 2, 3..." : "Enter chapter number (e.g., 1, 2, 3...)"}
                value={selectedChapter || ''}
                onChange={(e) => setSelectedChapter(Number(e.target.value))}
                helperText={isMobile ? "Enter chapter number manually" : "No chapters found in outline. Enter chapter number manually or generate outline first."}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            )}
            <TextField
              fullWidth
              label="Chapter Title"
              value={chapterTitle}
              onChange={(e) => setChapterTitle(e.target.value)}
              disabled={!selectedChapter}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Chapter Summary"
              placeholder={isMobile ? "Brief overview..." : "Brief overview of what happens in this chapter..."}
              value={chapterSummary}
              onChange={(e) => setChapterSummary(e.target.value)}
              disabled={!selectedChapter}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            {currentSession && (
              currentSession.characters.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Characters' : 'Characters Present'}
                  </InputLabel>
                  <Select
                    multiple
                    value={chapterCharacters}
                    onChange={(e) => setChapterCharacters(e.target.value as string[])}
                    label={isMobile ? 'Characters' : 'Characters Present'}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                        ))}
                      </Box>
                    )}
                  >
                    {currentSession.characters.map((char) => (
                      <MenuItem key={char.name} value={char.name} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <ListItemText 
                          primary={char.name}
                          secondary={char.role || char.description?.substring(0, 50) + '...'}
                          primaryTypographyProps={{
                            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: { xs: '0.75rem', sm: '0.8125rem' } }
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label={isMobile ? 'Characters' : 'Characters Present (comma-separated)'}
                  placeholder={isMobile ? "Adam, Eve, Cain" : "Enter character names, e.g., Adam, Eve, Cain"}
                  value={chapterCharacters.join(', ')}
                  onChange={(e) => {
                    const names = e.target.value.split(',').map(n => n.trim()).filter(n => n);
                    setChapterCharacters(names);
                  }}
                  helperText={isMobile ? "Enter names manually" : "No characters found in outline. Enter names manually or go back to generate outline first."}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              )
            )}
            {currentSession && currentSession.locations.length > 0 && (
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Locations' : 'Locations Present'}
                </InputLabel>
                <Select
                  multiple
                  value={chapterLocations}
                  onChange={(e) => setChapterLocations(e.target.value as string[])}
                  label={isMobile ? 'Locations' : 'Locations Present'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                      ))}
                    </Box>
                  )}
                >
                  {currentSession.locations.map((loc) => (
                    <MenuItem key={loc.name} value={loc.name} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label={isMobile ? 'Instructions' : 'Specific Instructions'}
              placeholder={isMobile ? "Special requirements..." : "Any specific requirements for this chapter..."}
              value={chapterInstructions}
              onChange={(e) => setChapterInstructions(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              type="number"
              label={isMobile ? 'Word Count' : 'Target Word Count'}
              value={chapterWordCount}
              onChange={(e) => setChapterWordCount(parseInt(e.target.value) || 4000)}
              inputProps={{ min: 1000, max: 10000, step: 500 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: { xs: 2, sm: 1 } }}>
          <Button 
            onClick={() => setChapterDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateChapter} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <EditIcon />)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {loading ? 'Writing...' : (isMobile ? 'Generate' : 'Generate Chapter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Chapter Dialog */}
      <Dialog
        open={viewChapterDialog}
        onClose={() => {
          setViewChapterDialog(false);
          setViewingChapter(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {viewingChapter && (
            <>
              Chapter {viewingChapter.chapterNumber}: {viewingChapter.title || 'Untitled'}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {viewingChapter && (
            <Box
              sx={{
                '& h1': { fontSize: { xs: '1.5rem', sm: '1.75rem' }, mt: 2, mb: 1 },
                '& h2': { fontSize: { xs: '1.25rem', sm: '1.5rem' }, mt: 2, mb: 1 },
                '& h3': { fontSize: { xs: '1.125rem', sm: '1.25rem' }, mt: 1.5, mb: 0.5 },
                '& h4': { fontSize: { xs: '1rem', sm: '1.125rem' }, mt: 1, mb: 0.5 },
                '& p': { mb: 1.5, fontSize: { xs: '0.9375rem', sm: '1rem' }, lineHeight: 1.7 },
                '& ul, & ol': { mb: 1.5, fontSize: { xs: '0.9375rem', sm: '1rem' } },
                '& li': { ml: 2, mb: 0.5 },
                '& strong': { fontWeight: 600 },
                '& em': { fontStyle: 'italic' },
                '& blockquote': {
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 2,
                  fontStyle: 'italic',
                  fontSize: { xs: '0.9375rem', sm: '1rem' }
                },
                '& code': {
                  backgroundColor: 'grey.100',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontSize: '0.9em',
                  fontFamily: 'monospace'
                },
                '& pre': {
                  backgroundColor: 'grey.100',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }
              }}
            >
              {viewingChapter.summary && (
                <Box sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }} gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2" sx={{
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    color: 'rgba(255, 255, 255, 0.85)'
                  }}>
                    {viewingChapter.summary}
                  </Typography>
                </Box>
              )}
              <ReactMarkdown>{viewingChapter.content || ''}</ReactMarkdown>
              {viewingChapter.wordCount && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    Word Count: {viewingChapter.wordCount.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: { xs: 2, sm: 1 } }}>
          <Button
            onClick={() => {
              if (viewingChapter) {
                handleCopyChapter(viewingChapter);
              }
            }}
            startIcon={!isMobile && <CopyIcon />}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {isMobile ? 'Copy' : 'Copy Chapter'}
          </Button>
          <Button
            onClick={() => {
              setViewChapterDialog(false);
              setViewingChapter(null);
            }}
            variant="contained"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NovelWritingAgent;